import { computed, signal, Signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';

export interface SmartListConfig {
  /** Field used for deduplication (default: 'id') */
  valueField?: string;
  /** Field used for local text filtering (default: 'name') */
  textField?: string;
  /** Query parameter name for search (default: 'q') */
  searchParam?: string;
  /** Number of results per API request (default: 100) */
  pageSize?: number;
  /** API base URL (default: environment.apiUrl) */
  apiBaseUrl?: string;
  /** Extra query parameters appended to every request */
  extraParams?: string;
}

const DEFAULTS: Required<SmartListConfig> = {
  valueField: 'id',
  textField: 'name',
  searchParam: 'q',
  pageSize: 100,
  apiBaseUrl: environment.apiUrl,
  extraParams: '',
};

/**
 * BaseSmartList — Abstract reactive data source with progressive merge caching.
 *
 * Manages a local `itemMap` that accumulates items across multiple searches.
 * On each search it filters locally first (instant), then fetches from the
 * server if the URL hasn't been seen before. Server results are merged
 * (deduplicated by `valueField`) and the filtered view is re-emitted.
 *
 * Subclasses implement `extractItems` and `extractCount` for their response format.
 */
export abstract class BaseSmartList<T = any> {
  // --- Reactive signals ---
  private readonly _items = signal<T[]>([]);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _totalCount = signal<number>(0);
  private readonly _currentOffset = signal<number>(0);

  readonly items: Signal<T[]> = this._items.asReadonly();
  readonly isLoading: Signal<boolean> = this._isLoading.asReadonly();
  readonly totalCount: Signal<number> = this._totalCount.asReadonly();
  readonly currentOffset: Signal<number> = this._currentOffset.asReadonly();

  /** True when more items are available beyond what has been fetched */
  readonly hasMore = computed(() => {
    const total = this._totalCount();
    return total > 0 && this._currentOffset() < total;
  });

  // --- Internal state ---
  private itemMap = new Map<any, T>();
  private fetchedUrls = new Set<string>();
  private requestVersion = 0;
  private currentQuery = '';
  private url = '';
  private config: Required<SmartListConfig>;

  constructor(
    private http: HttpClient,
    config?: SmartListConfig,
  ) {
    this.config = { ...DEFAULTS, ...config };
  }

  // --- Abstract methods ---

  protected abstract extractItems(response: any): T[];
  protected abstract extractCount(response: any, fallback: number): number;

  // --- Configuration ---

  /** Update config properties. Does NOT clear cache — call clearCache() if URLs change. */
  updateConfig(partial: Partial<SmartListConfig>): void {
    Object.assign(this.config, partial);
  }

  // --- URL management ---

  /** Set the endpoint path and optionally trigger an initial fetch. */
  setUrl(url: string, autoFetch = true): void {
    this.url = url;
    this.reset();
    if (autoFetch) {
      this.search('');
    }
  }

  getUrl(): string {
    return this.url;
  }

  // --- Search ---

  /**
   * 1. Filter local cache immediately -> emit to `items`.
   * 2. If the URL hasn't been fetched yet -> HTTP GET, merge, re-filter, emit.
   */
  search(query: string): void {
    this.currentQuery = query;
    this._currentOffset.set(0);

    // 1. Local filter — instant
    this._items.set(this.filterLocal(query));

    if (!this.url) return;

    // 2. Build URL and check cache
    const fullUrl = this.buildUrl(query, 0);
    if (this.fetchedUrls.has(fullUrl)) {
      return;
    }

    // 3. Fetch from server
    this._isLoading.set(true);
    const version = ++this.requestVersion;

    this.http.get<any>(`${this.config.apiBaseUrl}/${fullUrl}`).subscribe({
      next: (response) => {
        if (version !== this.requestVersion) return;

        const newItems = this.extractItems(response);
        const count = this.extractCount(response, newItems.length);

        // Merge into itemMap (dedup by valueField)
        for (const item of newItems) {
          this.itemMap.set((item as any)[this.config.valueField], item);
        }

        this._currentOffset.set(newItems.length);
        this.fetchedUrls.add(fullUrl);
        this._totalCount.set(count);
        this._items.set(this.filterLocal(this.currentQuery));
        this._isLoading.set(false);
      },
      error: () => {
        if (version !== this.requestVersion) return;
        this._isLoading.set(false);
      },
    });
  }

  /**
   * Fetch the next page of results for the current query.
   * Appends items to the existing cache without resetting.
   */
  searchMore(): void {
    if (!this.url || this._isLoading() || !this.hasMore()) return;

    const skip = this._currentOffset();
    const fullUrl = this.buildUrl(this.currentQuery, skip);
    if (this.fetchedUrls.has(fullUrl)) return;

    this._isLoading.set(true);
    const version = this.requestVersion; // Do NOT increment — continuation of current query

    this.http.get<any>(`${this.config.apiBaseUrl}/${fullUrl}`).subscribe({
      next: (response) => {
        if (version !== this.requestVersion) return; // stale if search() ran in between
        const newItems = this.extractItems(response);
        const count = this.extractCount(response, skip + newItems.length);

        for (const item of newItems) {
          this.itemMap.set((item as any)[this.config.valueField], item);
        }

        this._currentOffset.set(skip + newItems.length);
        this.fetchedUrls.add(fullUrl);
        this._totalCount.set(count);
        this._items.set(this.filterLocal(this.currentQuery));
        this._isLoading.set(false);
      },
      error: () => {
        if (version !== this.requestVersion) return;
        this._isLoading.set(false);
      },
    });
  }

  getCurrentQuery(): string {
    return this.currentQuery;
  }

  // --- Control ---

  /** Clear everything: items, cache, fetched URLs. */
  reset(): void {
    this.requestVersion++;
    this.itemMap.clear();
    this.fetchedUrls.clear();
    this.currentQuery = '';
    this._currentOffset.set(0);
    this._items.set([]);
    this._totalCount.set(0);
    this._isLoading.set(false);
  }

  /** Clear fetched URL tracking and itemMap but keep the URL. */
  clearCache(): void {
    this.requestVersion++;
    this.itemMap.clear();
    this.fetchedUrls.clear();
    this._currentOffset.set(0);
    this._items.set([]);
    this._totalCount.set(0);
    this._isLoading.set(false);
  }

  /** Remove a specific URL from fetchedUrls so it will be re-fetched. */
  invalidateUrl(url: string): void {
    this.fetchedUrls.delete(url);
  }

  /** Cancel in-flight requests and clear all state. */
  destroy(): void {
    this.reset();
    this.url = '';
  }

  // --- Data access ---

  get length(): number {
    return this._items().length;
  }

  toArray(): T[] {
    return [...this._items()];
  }

  /** Find an item in the full cache by its valueField. */
  find(id: any): T | undefined {
    return this.itemMap.get(id);
  }

  /** Check if an item exists in the full cache. */
  has(id: any): boolean {
    return this.itemMap.has(id);
  }

  /** Return all cached items (unfiltered). */
  getAllCached(): T[] {
    return Array.from(this.itemMap.values());
  }

  // --- Manual manipulation ---

  /** Add an item to the cache (and update filtered view). */
  addItem(item: T): void {
    this.itemMap.set((item as any)[this.config.valueField], item);
    this._items.set(this.filterLocal(this.currentQuery));
  }

  /** Remove an item from the cache by its valueField key. */
  removeItem(id: any): void {
    this.itemMap.delete(id);
    this._items.set(this.filterLocal(this.currentQuery));
  }

  // --- Private helpers ---

  private filterLocal(query: string): T[] {
    const allItems = Array.from(this.itemMap.values());
    if (!query) return allItems;

    const lowerQuery = query.toLowerCase();
    return allItems.filter((item) =>
      String((item as any)[this.config.textField] ?? '')
        .toLowerCase()
        .includes(lowerQuery),
    );
  }

  private buildUrl(query: string, skip = 0): string {
    let url = this.url;
    const params: string[] = [];

    if (query) params.push(`${this.config.searchParam}=${encodeURIComponent(query)}`);
    params.push(`$top=${this.config.pageSize}`);
    params.push(`$skip=${skip}`);
    if (this.config.extraParams) params.push(this.config.extraParams);

    if (params.length > 0) {
      const separator = url.includes('?') ? '&' : '?';
      url += separator + params.join('&');
    }
    return url;
  }
}

/**
 * PaginatedSmartList — For Django REST paginated responses.
 *
 * Handles:
 * - Direct arrays: `[{...}, {...}]`
 * - Paginated objects: `{ count: N, results: [{...}] }`
 */
export class PaginatedSmartList<T = any> extends BaseSmartList<T> {
  protected extractItems(response: any): T[] {
    if (Array.isArray(response)) return response;
    if (response?.results) return response.results;
    return [];
  }

  protected extractCount(response: any, fallback: number): number {
    return response?.count ?? fallback;
  }
}

/**
 * ODataSmartList — For OData v4 responses.
 *
 * Handles:
 * - Direct arrays: `[{...}, {...}]`
 * - OData objects: `{ '@odata.count': N, value: [{...}] }`
 */
export class ODataSmartList<T = any> extends BaseSmartList<T> {
  protected extractItems(response: any): T[] {
    if (Array.isArray(response)) return response;
    if (response?.value) return response.value;
    return [];
  }

  protected extractCount(response: any, fallback: number): number {
    return response?.['@odata.count'] ?? fallback;
  }
}

/** @deprecated Use PaginatedSmartList or ODataSmartList */
export const SmartList = PaginatedSmartList;
/** @deprecated Use PaginatedSmartList or ODataSmartList */
export type SmartList<T = any> = PaginatedSmartList<T>;
